import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';

/**
 * Keeps the Firebase web SDK out of AppModule and therefore off the native iOS
 * startup path. iOS push continues to use the Capacitor PushNotifications
 * bridge; browsers can initialize Firebase Messaging after their first paint.
 */
@Injectable({ providedIn: 'root' })
export class DeferredWebFirebaseService {
  private initialization?: Promise<void>;

  initialize(): Promise<void> {
    if (Capacitor.isNativePlatform()) return Promise.resolve();
    if (this.initialization) return this.initialization;

    // Some production deployments intentionally omit the web Firebase client
    // configuration. Native push does not use this object, and attempting to
    // initialize the web SDK with placeholder values only adds startup work.
    const config = (environment as any).firebaseConfig;
    if (!config || !config.projectId || config.projectId === 'YOUR_PROJECT_ID') {
      return Promise.resolve();
    }

    this.initialization = Promise.all([
      import('firebase/app'),
      import('firebase/messaging'),
    ]).then(async ([firebaseApp, firebaseMessaging]) => {
      const app = firebaseApp.getApps().length
        ? firebaseApp.getApp()
        : firebaseApp.initializeApp(config);

      if (await firebaseMessaging.isSupported()) {
        firebaseMessaging.getMessaging(app);
      }
    }).catch(error => {
      this.initialization = undefined;
      console.warn('Deferred Firebase web initialization failed', error);
    });

    return this.initialization;
  }
}
